/**
 * Review Funnel Service (public / no-login)
 *
 * The client taps the review link in their text/email → lands on ReviewFunnelPage →
 * this service loads their request (by token), records the star rating, and traps the
 * click-through to Google. It is the "trap, trace, document" layer Jose asked for: we
 * can't see the actual Google post, but we log the rating + whether/when they clicked
 * through to Google, tied to the client + barber + appointment.
 *
 * COMPLIANCE (Google review policy + FTC 2024): we do NOT gate. Everyone can reach
 * Google. 5★ gets a big Google CTA; below-threshold gets a private "what went wrong"
 * channel FIRST but STILL shows a Google link. See ReviewFunnelPage.
 *
 * Config lives in publicConfig/reviewSettings (mirrored from POS Settings, like hours).
 * BEHAVIOR-PIN: SLIK review funnel — project-slik-pos review-request system.
 */
import { doc, getDoc, updateDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface ReviewSettings {
  enabled: boolean;
  googleReviewLink: string;      // pasted "get more reviews" link (g.page/r/.../review) — preferred, no Place ID hunting
  googlePlaceId: string;         // fallback: shop's Google Place ID
  publicThreshold: number;       // rating ≥ this → push to Google prominently (default 5)
  privateFeedbackEnabled: boolean;
  shopName: string;
  headline: string;              // e.g. "How was your visit?"
  thankYouPublic: string;        // shown after a high rating
  thankYouPrivate: string;       // shown after private feedback
}

const DEFAULT_SETTINGS: ReviewSettings = {
  enabled: true,
  googleReviewLink: 'https://g.page/r/CVhCEdueRX6eEBM/review', // SLIK Barber Lounge; POS Settings overrides per shop
  googlePlaceId: '',
  publicThreshold: 5,
  privateFeedbackEnabled: true,
  shopName: 'SLIK Barber Lounge',
  headline: 'How was your visit?',
  thankYouPublic: 'Thank you! A quick public review means the world to us. 🙏',
  thankYouPrivate: "Thank you — we're sorry it wasn't perfect. Your note goes straight to the owner.",
};

export interface ReviewRequest {
  id: string;               // token
  clientId?: string;
  clientName?: string;
  barberId?: string;
  barberName?: string;
  appointmentId?: string;
  rating?: number;          // captured on this page
  googleClickedAt?: any;
  status?: 'sent' | 'rated' | 'google_clicked' | 'private_feedback';
  isDemo?: boolean;
}

/** Where "leave a Google review" goes. Prefer the pasted link; else Place ID; else search. */
export function googleReviewUrl(settings: ReviewSettings): string {
  if (settings.googleReviewLink) return settings.googleReviewLink;
  if (settings.googlePlaceId) return `https://search.google.com/local/writereview?placeid=${settings.googlePlaceId}`;
  return 'https://www.google.com/search?q=' + encodeURIComponent(settings.shopName + ' reviews');
}

export async function getReviewSettings(): Promise<ReviewSettings> {
  try {
    const snap = await getDoc(doc(db, 'publicConfig', 'reviewSettings'));
    if (snap.exists()) return { ...DEFAULT_SETTINGS, ...(snap.data() as Partial<ReviewSettings>) };
  } catch (e) {
    console.warn('reviewSettings read failed, using defaults', e);
  }
  return DEFAULT_SETTINGS;
}

/** Load the request behind a token. 'demo' returns a synthetic one (no writes). */
export async function getReviewRequest(token: string): Promise<ReviewRequest | null> {
  if (token === 'demo') {
    return { id: 'demo', clientName: 'there', barberName: 'your barber', isDemo: true, status: 'sent' };
  }
  try {
    const snap = await getDoc(doc(db, 'reviewRequests', token));
    if (!snap.exists()) return null;
    return { id: snap.id, ...(snap.data() as any) };
  } catch (e) {
    console.warn('reviewRequest read failed', e);
    return null;
  }
}

async function patchRequest(req: ReviewRequest, patch: Record<string, any>) {
  if (req.isDemo) return;
  try {
    await updateDoc(doc(db, 'reviewRequests', req.id), patch);
  } catch {
    // First write may need create (defensive) — merge.
    await setDoc(doc(db, 'reviewRequests', req.id), patch, { merge: true });
  }
}

/** Record the star rating the client tapped (all ratings, all sentiments — first-party asset). */
export async function recordStarRating(req: ReviewRequest, rating: number): Promise<void> {
  await patchRequest(req, { rating, ratedAt: serverTimestamp(), status: 'rated' });
}

/** They tapped through to Google — trap + timestamp it (we can't see the post itself). */
export async function recordGoogleClick(req: ReviewRequest): Promise<void> {
  await patchRequest(req, { googleClickedAt: serverTimestamp(), status: 'google_clicked' });
}

/**
 * Below-threshold private feedback → lands in our own reviews store (service recovery +
 * the SLIK first-party review asset), and marks the request.
 */
export async function submitPrivateFeedback(req: ReviewRequest, rating: number, text: string): Promise<void> {
  if (!req.isDemo) {
    await addDoc(collection(db, 'reviews'), {
      customerId: req.clientId || null,
      customerName: req.clientName || 'Guest',
      barberId: req.barberId || null,
      barberName: req.barberName || null,
      appointmentId: req.appointmentId || null,
      rating,
      text,
      source: 'review_funnel',
      visibility: 'private',   // owner-only until promoted
      createdAt: serverTimestamp(),
    });
  }
  await patchRequest(req, { rating, privateFeedback: text, status: 'private_feedback', ratedAt: serverTimestamp() });
}
